import { useCallback, useRef, useState } from "react";
import { Heart, Spade } from "lucide-react";
import MarketScanner from "./MarketScanner";
import HowToPlay from "./HowToPlay";
import { useAutoRun, AutoRunBar } from "./AutoRun";
import { MarqueeRow, StageFX, StreakFlame } from "./CasinoFX";
import "./arcade-anim.css";

/** Red/Black — casino table: the croupier deals, a 3D card flip reveals the
 * real settled digit. Red = EVEN, Black = ODD. Even/Odd Signal Engine finds
 * streaky markets; ⚡ one-tap plays the detected side. */
const RedBlackFlip = ({ runFlip, busy }) => {
  const [flip, setFlip] = useState(null); // {state: flipping|done, res}
  const [tally, setTally] = useState({ w: 0, l: 0, net: 0 });
  const [wstreak, setWstreak] = useState(0);
  const [mkt, setMkt] = useState(null);
  const symRef = useRef("1HZ100V");
  const mktRef = useRef(null);
  const onScan = useCallback((row) => { symRef.current = row.sym; mktRef.current = row; setMkt(row); }, []);

  const pick = async (side) => {
    if (flip?.state === "flipping") return null;
    setFlip({ state: "flipping" });
    const res = await runFlip(side, null, "Red/Black Flip", symRef.current);
    if (!res) { setFlip(null); return null; }
    setFlip({ state: "done", res });
    setWstreak((s) => (res.won ? s + 1 : 0));
    setTally((t) => ({ w: t.w + (res.won ? 1 : 0), l: t.l + (res.won ? 0 : 1), net: Math.round((t.net + res.profit) * 100) / 100 }));
    return res;
  };
  const auto = useAutoRun("redblack");

  const res = flip?.res;
  const isRed = res && res.digit != null && res.digit % 2 === 0;
  const flipping = flip?.state === "flipping";

  return (
    <div data-testid="redblack-game" className="space-y-4">
      <style>{`
        .vts-card3d{perspective:800px}
        .vts-card-inner{transition:transform .8s cubic-bezier(.3,.9,.3,1);transform-style:preserve-3d;position:relative;width:100%;height:100%}
        .vts-card-inner.flipped{transform:rotateY(180deg)}
        .vts-face{position:absolute;inset:0;backface-visibility:hidden;display:flex;flex-direction:column;align-items:center;justify-content:center;border-radius:12px;gap:4px}
        .vts-face.back{transform:rotateY(180deg)}
        @keyframes vts-shuffle{0%,100%{transform:rotate(0)}25%{transform:rotate(-4deg)}75%{transform:rotate(4deg)}}
      `}</style>

      <HowToPlay id="redblack"
        steps={[
          "The ⚡ Even/Odd Signal Engine scans all 10 markets for streaks and lopsided runs (e.g. '7 of last 10 digits even') and locks the strongest — or says WAIT when everything is balanced.",
          "Tap ⚡ PLAY THE SIGNAL to take the detected colour, or choose yourself: RED bets the next digit is EVEN, BLACK bets it's ODD.",
          "The croupier deals — the card flips to show the REAL settled digit of the next tick.",
          "Red card with your Red pick (or black with Black) = you win.",
        ]}
        win="the next tick's last digit matches your colour — Red = even digit (0·2·4·6·8), Black = odd (1·3·5·7·9)."
        payout="~×1.95 your stake per winning card. Every deal is close to 50/50."
        tip="set a session budget and a stop-win (e.g. +5 cards) — the table never runs out of cards, you decide when to leave." />

      <MarketScanner id="redblack" mode="evenodd" onChange={onScan}
        note="E/O = even vs odd share of the last 50 digits · streaks are history, every next tick stays ~50/50" />

      {/* 🎩 casino table */}
      <div className="relative rounded-xl overflow-hidden border-2 border-[#F7B731]/50"
        style={{ background: "radial-gradient(ellipse at 50% 20%, #14532d 0%, #0c3320 55%, #082617 100%)", boxShadow: "0 0 22px rgba(247,183,49,.1), inset 0 0 40px rgba(0,0,0,.45)" }}>
        <MarqueeRow pos="top" />
        <MarqueeRow pos="bottom" />
        <StageFX outcome={res && flip?.state === "done" ? (res.won ? "win" : "loss") : null} />
        {/* croupier */}
        <svg viewBox="0 0 320 70" className="w-full h-16">
          <g transform="translate(160,8)">
            <g className={`arc-fig ${flipping ? "" : "arc-bob"}`}>
              <circle cx="0" cy="18" r="10" fill="#E8B88A" />
              <circle cx="-3.5" cy="16" r="1.4" fill="#0B0E14" />
              <circle cx="3.5" cy="16" r="1.4" fill="#0B0E14" />
              <path d="M -10 14 A 10 10 0 0 1 10 14" fill="#0B0E14" />
              <path d="M -15 52 L -10 29 Q 0 25 10 29 L 15 52 Z" fill="#111827" />
              <path d="M -3.5 31 L 0 34.5 L 3.5 31 L 0 29 Z" fill="#dc2626" />
              <line x1="-11" y1="34" x2="-24" y2={flipping ? "48" : "44"} stroke="#111827" strokeWidth="4.5" strokeLinecap="round" />
              <line x1="11" y1="34" x2="24" y2={flipping ? "48" : "44"} stroke="#111827" strokeWidth="4.5" strokeLinecap="round" />
            </g>
          </g>
          {/* chip stacks */}
          <g>
            {[46, 54, 62].map((y, i) => <rect key={i} x="36" y={y} width="26" height="6" rx="3" fill={i % 2 ? "#dc2626" : "#F7B731"} stroke="#0B0E14" strokeWidth="1" />)}
            {[50, 58].map((y, i) => <rect key={i} x="258" y={y} width="26" height="6" rx="3" fill={i % 2 ? "#F7B731" : "#111827"} stroke="#0B0E14" strokeWidth="1" />)}
          </g>
        </svg>

        <div className="flex justify-center items-end gap-4 pb-4 -mt-1">
          {/* 🂠 the deck — cards slide out of it on every deal */}
          <div className="relative w-16 h-24 hidden sm:block">
            {[0, 1, 2].map((i) => (
              <div key={i} className="absolute inset-0 rounded-lg border border-white/15"
                style={{ background: "repeating-linear-gradient(135deg,#16213c 0 8px,#0f1729 8px 16px)", transform: `translate(${i * 2}px,${-i * 2}px) rotate(${i - 1}deg)`, boxShadow: "0 4px 10px rgba(0,0,0,.4)" }} />
            ))}
          </div>
          <div className={`vts-card3d w-36 h-52 ${flipping ? "deal-slide" : ""}`} style={flipping ? { animation: "vts-shuffle .4s ease infinite" } : {}}>
            <div className={`vts-card-inner ${flip?.state === "done" ? "flipped" : ""}`}>
              <div className="vts-face border border-white/15" style={{ background: "repeating-linear-gradient(135deg,#16213c 0 8px,#0f1729 8px 16px)", boxShadow: "0 6px 22px rgba(0,0,0,.5)" }}>
                <Spade className="w-9 h-9 text-[#2962FF]" style={{ filter: "drop-shadow(0 0 6px rgba(41,98,255,.6))" }} />
                <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-white/50">{flipping ? "dealing…" : "VTS deck"}</span>
              </div>
              <div className="vts-face back border"
                style={{ background: "linear-gradient(#f8fafc,#e2e8f0)", borderColor: isRed ? "#dc2626" : "#111827" }}>
                <span className="text-5xl font-black font-mono" style={{ color: isRed ? "#dc2626" : "#111827" }}>{res?.digit ?? "?"}</span>
                {isRed ? <Heart className="w-7 h-7 text-[#dc2626]" fill="#dc2626" /> : <Spade className="w-7 h-7 text-[#111827]" fill="#111827" />}
                {res && <span className={`text-xs font-bold font-mono ${res.won ? "text-emerald-600" : "text-red-600"}`}>{res.won ? `+$${res.profit.toFixed(2)}` : `−$${Math.abs(res.profit).toFixed(2)}`}</span>}
              </div>
            </div>
          </div>
        </div>
        {/* 🔥 hot hand */}
        {wstreak >= 2 && <div className="absolute left-2 bottom-2 z-20"><StreakFlame n={wstreak} /></div>}
      </div>

      <AutoRunBar id="redblack" auto={auto} noun="cards" />

      {mkt?.signal && !mkt.signal.wait ? (
        <button data-testid="redblack-signal-bet" disabled={busy || flipping || auto.active} onClick={() => {
          let last = mkt.signal.side;
          auto.burst(pick, () => { const s = mktRef.current?.signal; if (s && !s.wait) last = s.side; return last; });
        }}
          className="w-full h-12 rounded-lg bg-[#F7B731] hover:brightness-110 text-[#0B0E14] font-black text-sm uppercase tracking-wide transition disabled:opacity-40 active:scale-[0.99]">
          ⚡ Play the signal — {mkt.signal.side === "EVEN" ? "RED (even)" : "BLACK (odd)"} · {mkt.signal.strength}/100{auto.runs > 1 ? " · follows the best signal" : ""}
        </button>
      ) : (
        <div data-testid="redblack-signal-wait" className="w-full h-12 rounded-lg border border-dashed border-white/15 grid place-items-center text-[10px] font-mono text-white/45">
          ⏳ no clear run right now — the table is balanced, the engine re-scans every 12s
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button data-testid="redblack-red" disabled={busy || auto.active} onClick={() => auto.burst(pick, "EVEN")}
          className="h-16 rounded-lg bg-[#dc2626] hover:bg-[#b91c1c] text-white transition-colors disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight">
          <span className="inline-flex items-center gap-2 font-black text-sm uppercase tracking-wide"><Heart className="w-4 h-4" fill="currentColor" /> Red · Even{mkt && mkt.evenPct >= 56 ? " 🔥" : ""}</span>
          <span className="text-[10px] font-mono text-white/75">wins on 0 · 2 · 4 · 6 · 8{mkt ? ` · recent E ${mkt.evenPct}%` : ""}</span>
        </button>
        <button data-testid="redblack-black" disabled={busy || auto.active} onClick={() => auto.burst(pick, "ODD")}
          className="h-16 rounded-lg bg-[#0B0E14] border border-white/25 hover:bg-white/5 text-white transition-colors disabled:opacity-40 active:scale-[0.98] flex flex-col items-center justify-center leading-tight">
          <span className="inline-flex items-center gap-2 font-black text-sm uppercase tracking-wide"><Spade className="w-4 h-4" fill="currentColor" /> Black · Odd{mkt && mkt.evenPct <= 44 ? " 🔥" : ""}</span>
          <span className="text-[10px] font-mono text-white/60">wins on 1 · 3 · 5 · 7 · 9{mkt ? ` · recent O ${100 - mkt.evenPct}%` : ""}</span>
        </button>
      </div>
      <p data-testid="redblack-tally" className="text-center text-[11px] font-mono text-white/60">
        Hits <span className="text-[#089981] font-bold">{tally.w}</span> · Misses <span className="text-[#F23645] font-bold">{tally.l}</span> · Net <b className={tally.net >= 0 ? "text-[#089981]" : "text-[#F23645]"}>${tally.net.toFixed(2)}</b>
      </p>
      <p className="text-center text-[10px] font-mono text-white/40">every card is a real Deriv Even/Odd contract (~×1.95) on the locked market · each deal stays ~50/50 · losses possible</p>
    </div>
  );
};

export default RedBlackFlip;
